import React, { useEffect, useState } from 'react';
import { useSelectionContext } from 'Compass/profile-lab/contexts/SelectionContext';
import { getUserLid } from 'Compass/profile-lab/hooks/useUserLid';
import { computeSelection } from 'Compass/profile-lab/services/ProfileLabService';
import { ButtonClear, ButtonCommit, ButtonGroup } from 'Compass/profile-lab/styles/buttons';
import {
  FormCheckbox,
  FormInputNumber,
  FormRow,
  FormSubmit,
  SelectionGrowStatus,
  SelectionPage,
  SelectionStatus,
  Tab,
  TabBar,
  TabContent
} from 'Compass/profile-lab/styles/selection';

type TabName = 'random' | 'latest' | 'ranked' | 'stage';

const ProfileLabSelectionPage: React.FC = () => {
  const { growCount, selectedCount, totalCount, refreshCounts } = useSelectionContext();
  const [activeTab, setActiveTab] = useState<TabName>('random');

  useEffect(() => {
    const saved = localStorage.getItem('selectedTab');
    if (saved) {
      setActiveTab(saved as TabName);
    }
  }, []);

  const switchTab = (tab: TabName) => {
    setActiveTab(tab);
    localStorage.setItem('selectedTab', tab);
  };

  const handleCompute = async (operation: string, params: Record<string, string | number | boolean>) => {
    try {
      const { db } = await import('../services/db');
      await db.init();

      const userLid = await getUserLid();

      const allPhotos = await db.getAllPhotos();
      const selectionState = await db.getSelectionState();
      const photoIds = allPhotos.map((photo) => photo.photo_lid);

      const growIds = await computeSelection(userLid, operation, photoIds, selectionState.selected, params);

      await db.updateSelection({ grow: growIds });
      await refreshCounts();
    } catch (error) {
      console.error('Selection compute failed:', error);
    }
  };

  const handleLocalOperation = async (operation: 'commit' | 'clear') => {
    try {
      const { db } = await import('../services/db');
      await db.init();

      const selectionState = await db.getSelectionState();

      if (operation === 'commit') {
        const newSelected = [...new Set([...selectionState.selected, ...selectionState.grow])];
        await db.updateSelection({ selected: newSelected, grow: [] });
      } else if (operation === 'clear') {
        await db.updateSelection({ selected: [], grow: [] });
      }

      await refreshCounts();
    } catch (error) {
      console.error(`Operation ${operation} failed:`, error);
    }
  };

  const useFormState = <T extends Record<string, string | number | boolean>>(initialState: T) => {
    const [values, setValues] = useState<T>(initialState);

    useEffect(() => {
      const savedValues: Record<string, string | number | boolean> = {};
      Object.keys(initialState).forEach((key) => {
        const saved = localStorage.getItem(`input_${key}`);
        if (saved !== null) {
          const numValue = Number(saved);
          savedValues[key] = isNaN(numValue) ? saved : numValue;
        }
      });
      setValues((prev) => ({ ...prev, ...savedValues }));
    }, [initialState]);

    const handleChange = (key: keyof T, value: string | number | boolean) => {
      setValues((prev) => {
        const newValues = { ...prev, [key]: value };
        localStorage.setItem(`input_${String(key)}`, String(value));
        return newValues;
      });
    };

    return [values, handleChange] as const;
  };

  const RandomTab = () => {
    const [values, handleChange] = useFormState({
      random_count: 100,
      use_zipf: false,
    });

    useEffect(() => {
      const saved = localStorage.getItem('checkbox_use_zipf');
      if (saved !== null) {
        handleChange('use_zipf', saved === 'true');
      }
    }, [handleChange]);

    const onSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      void handleCompute('random', {
        count: values.random_count,
        use_zipf: values.use_zipf,
      });
    };

    return (
      <form onSubmit={onSubmit}>
        <FormRow>
          <label htmlFor="random_count">Number of photos to sample:</label>
          <FormInputNumber
            id="random_count"
            min={1}
            step={1}
            value={values.random_count}
            onChange={(e) => handleChange('random_count', Number(e.target.value))}
          />
        </FormRow>
        <FormRow>
          <label htmlFor="use_zipf">
            <FormCheckbox
              id="use_zipf"
              checked={values.use_zipf}
              onChange={(e) => {
                const checked = e.target.checked;
                handleChange('use_zipf', checked);
                localStorage.setItem('checkbox_use_zipf', String(checked));
              }}
            />
            Use Zipfian distribution (bias towards newer photos)
          </label>
        </FormRow>
        <FormSubmit value="Sample" />
      </form>
    );
  };

  const LatestTab = () => {
    const [values, handleChange] = useFormState({ latest_count: 100 });

    const onSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      void handleCompute('latest', { count: values.latest_count });
    };

    return (
      <form onSubmit={onSubmit}>
        <FormRow>
          <label htmlFor="latest_count">Number of latest photos:</label>
          <FormInputNumber
            id="latest_count"
            min={1}
            step={1}
            value={values.latest_count}
            onChange={(e) => handleChange('latest_count', Number(e.target.value))}
          />
        </FormRow>
        <FormSubmit value="Select Latest" />
      </form>
    );
  };

  const StageTab = () => {
    const [values, handleChange] = useFormState({
      stage_max_months: 0,
      stage_limit: 0,
      stage_only_self_identity: false,
      stage_has_faces: false,
    });

    const onSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const params: Record<string, string | number | boolean> = {
        only_self_identity: values.stage_only_self_identity,
        has_faces: values.stage_has_faces,
      };
      if (values.stage_max_months > 0) {
        params.max_months = values.stage_max_months;
      }
      if (values.stage_limit > 0) {
        params.limit = values.stage_limit;
      }
      void handleCompute('stage', params);
    };

    return (
      <form onSubmit={onSubmit}>
        <FormRow>
          <label htmlFor="stage_max_months">Max age of photos (months, 0 = all time):</label>
          <FormInputNumber
            id="stage_max_months"
            min={0}
            step={1}
            value={values.stage_max_months}
            onChange={(e) => handleChange('stage_max_months', Number(e.target.value))}
          />
        </FormRow>
        <FormRow>
          <label htmlFor="stage_limit">Maximum number of photos (0 = unlimited):</label>
          <FormInputNumber
            id="stage_limit"
            min={0}
            step={1}
            value={values.stage_limit}
            onChange={(e) => handleChange('stage_limit', Number(e.target.value))}
          />
        </FormRow>
        <FormRow>
          <label htmlFor="stage_has_faces">
            <FormCheckbox
              id="stage_has_faces"
              checked={values.stage_has_faces}
              onChange={(e) => handleChange('stage_has_faces', e.target.checked)}
            />
            Only photos with faces
          </label>
        </FormRow>
        <FormRow>
          <label htmlFor="stage_only_self_identity">
            <FormCheckbox
              id="stage_only_self_identity"
              checked={values.stage_only_self_identity}
              onChange={(e) => handleChange('stage_only_self_identity', e.target.checked)}
            />
            Only photos with the user&apos;s face
          </label>
        </FormRow>
        <FormSubmit value="Select Stage Style" />
      </form>
    );
  };

  const RankedTab = () => {
    const [values, handleChange] = useFormState({
      ranked_eps_hours: 24,
      ranked_eps_km: 1.0,
      ranked_desired_temporal_cluster_size: 5,
      ranked_desired_location_cluster_size: 5,
      ranked_temporal_weight: 100,
      ranked_location_weight: 100,
      ranked_recency_weight: 50.0,
      ranked_top_n: 1000,
    });

    const onSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      void handleCompute('ranked', {
        eps_hours: values.ranked_eps_hours,
        eps_km: values.ranked_eps_km,
        desired_temporal_cluster_size: values.ranked_desired_temporal_cluster_size,
        desired_location_cluster_size: values.ranked_desired_location_cluster_size,
        temporal_weight: values.ranked_temporal_weight,
        location_weight: values.ranked_location_weight,
        recency_weight: values.ranked_recency_weight,
        top_n: values.ranked_top_n,
      });
    };

    return (
      <form onSubmit={onSubmit}>
        <FormRow>
          <label htmlFor="ranked_eps_hours">Max time gap (hours):</label>
          <FormInputNumber
            id="ranked_eps_hours"
            min={0.1}
            step={0.1}
            value={values.ranked_eps_hours}
            onChange={(e) => handleChange('ranked_eps_hours', Number(e.target.value))}
          />
        </FormRow>
        <FormRow>
          <label htmlFor="ranked_eps_km">Max distance (km):</label>
          <FormInputNumber
            id="ranked_eps_km"
            min={0.1}
            step={0.1}
            value={values.ranked_eps_km}
            onChange={(e) => handleChange('ranked_eps_km', Number(e.target.value))}
          />
        </FormRow>
        <FormRow>
          <label htmlFor="ranked_desired_temporal_cluster_size">
            Desired temporal cluster size:
          </label>
          <FormInputNumber
            id="ranked_desired_temporal_cluster_size"
            min={1}
            value={values.ranked_desired_temporal_cluster_size}
            onChange={(e) =>
              handleChange('ranked_desired_temporal_cluster_size', Number(e.target.value))
            }
          />
        </FormRow>
        <FormRow>
          <label htmlFor="ranked_desired_location_cluster_size">
            Desired location cluster size:
          </label>
          <FormInputNumber
            id="ranked_desired_location_cluster_size"
            min={1}
            value={values.ranked_desired_location_cluster_size}
            onChange={(e) =>
              handleChange('ranked_desired_location_cluster_size', Number(e.target.value))
            }
          />
        </FormRow>
        <FormRow>
          <label htmlFor="ranked_temporal_weight">Temporal weight:</label>
          <FormInputNumber
            id="ranked_temporal_weight"
            min={0}
            step={1}
            value={values.ranked_temporal_weight}
            onChange={(e) => handleChange('ranked_temporal_weight', Number(e.target.value))}
          />
        </FormRow>
        <FormRow>
          <label htmlFor="ranked_location_weight">Location weight:</label>
          <FormInputNumber
            id="ranked_location_weight"
            min={0}
            step={1}
            value={values.ranked_location_weight}
            onChange={(e) => handleChange('ranked_location_weight', Number(e.target.value))}
          />
        </FormRow>
        <FormRow>
          <label htmlFor="ranked_recency_weight">Recency weight:</label>
          <FormInputNumber
            id="ranked_recency_weight"
            min={0}
            step={0.1}
            value={values.ranked_recency_weight}
            onChange={(e) => handleChange('ranked_recency_weight', Number(e.target.value))}
          />
        </FormRow>
        <FormRow>
          <label htmlFor="ranked_top_n">Number to select:</label>
          <FormInputNumber
            id="ranked_top_n"
            min={1}
            value={values.ranked_top_n}
            onChange={(e) => handleChange('ranked_top_n', Number(e.target.value))}
          />
        </FormRow>
        <FormSubmit value="Rank and Select" />
      </form>
    );
  };

  return (
    <SelectionPage>
      <h2>Selection</h2>
      <p>
        Currently selected:{' '}
        <SelectionStatus>{selectedCount}</SelectionStatus> / {totalCount} photos
      </p>
      <p>
        Grow: <SelectionGrowStatus>{growCount}</SelectionGrowStatus> photos
      </p>
      <ButtonGroup>
        <ButtonCommit onClick={() => handleLocalOperation('commit')}>
          Commit Grow → Selected
        </ButtonCommit>
        <ButtonClear onClick={() => handleLocalOperation('clear')}>
          Clear All
        </ButtonClear>
      </ButtonGroup>

      <TabBar>
        <Tab $active={activeTab === 'random'} onClick={() => switchTab('random')}>
          Random Sample
        </Tab>
        <Tab $active={activeTab === 'latest'} onClick={() => switchTab('latest')}>
          Latest
        </Tab>
        <Tab $active={activeTab === 'stage'} onClick={() => switchTab('stage')}>
          Stage Style
        </Tab>
        <Tab $active={activeTab === 'ranked'} onClick={() => switchTab('ranked')}>
          Ranked Addition
        </Tab>
      </TabBar>

      <TabContent $active={activeTab === 'random'}>
        <RandomTab />
      </TabContent>
      <TabContent $active={activeTab === 'latest'}>
        <LatestTab />
      </TabContent>
      <TabContent $active={activeTab === 'stage'}>
        <StageTab />
      </TabContent>
      <TabContent $active={activeTab === 'ranked'}>
        <RankedTab />
      </TabContent>
    </SelectionPage>
  );
};

export default ProfileLabSelectionPage;
